import { RouteProp, useRoute } from '@react-navigation/native';
import { BaseText, theme } from 'aimo-ui';
import CrickleCutCardBackground from 'components/common/CrickleCutCardBackground';
import Icon from 'components/icons/Icon.tsx';
import Attachment from 'components/longTermParking/Attachment';
import { PermitElementRow } from 'components/services/PermitElementRow.tsx';
import { isAfter } from 'date-fns';
import useUserPermitOrderLine from 'hooks/useUserPermitOrderLine.tsx';
import { useTranslation } from 'react-i18next';
import PermitTicketHeader from 'screens/permit/PermitTicketHeader';
import SalesOrderPermiPriceInfo from 'screens/permit/salesOrder/SalesOrderPermitPriceInfo';
import { PermitContentWrapper } from 'screens/permit/styles.tsx';
import { usePermitStore } from 'stores/permitStore';
import styled from 'styled-components/native';
import { StyledWrapper } from 'styles/ContainerStyles';
import { VoidCallBack } from 'types/common';
import {
  AdditionalDiscount,
  BillingFrequency,
  BillingType,
  CountryCode,
  Maybe,
  ParkZone,
  ParkingSpace,
  Pmc,
  Promotion,
  SalesLocation,
  SalesOrderStatus,
  UsageType,
} from 'types/generatedSchemaTypes';
import { RootStackParamList } from 'types/navigation.ts';
import { formatPermitValid, getParkingSpacesText } from 'utils/permitUtils.ts';
import MenuToggleItem from '../menuItems/MenuToggleItem';
import { useUiStore } from 'stores/uiStore';

const ListItemWrapper = styled.View`
  width: 100%;
`;

type ParkingInfo = PermitDetailsProps & AutoRenewalViewProps;
type PermitCardProps = ParkingInfo &
  ValidToProp & {
    /** When set, shown on the permit card (e.g. sales order scheduled termination). */
    scheduledTerminationDate?: Date;
    showAttachment?: boolean;
  };

type PermitDetailsProps = {
  parkZone: ParkZone;
  salesLocation: SalesLocation;
  permitName: string;
  price: number;
  pmc: Maybe<Pmc> | undefined;
  useAdditionalDiscount: boolean;
  additionalDiscount: Maybe<AdditionalDiscount> | undefined;
  promotion: Maybe<Promotion> | undefined;
  testID?: string;
  billingFrequency: BillingFrequency;
  billingType: BillingType;
  parkingSpaceName?: string;
  validTo: Maybe<Date> | undefined;
  validFrom: Maybe<Date> | undefined;
  quantity?: Maybe<number>;
  onPress?: () => void;
  country: CountryCode;
  usageType?: UsageType;
  serviceFee: number;
};

type AutoRenewalViewProps = {
  isRecurring: boolean;
  setIsRecurring: VoidCallBack;
  testID?: string;
  status: SalesOrderStatus;
  isInvoicing: boolean;
  allowRecurring: boolean;
  /** When true, the renewal toggle is non-interactive (e.g. sales order forbids changes). */
  isRenewalDisabled?: boolean;
  noBorder?: boolean;
};

type ValidToProp = {
  validTo: Maybe<Date> | undefined;
};

const SelectListItem = ({
  title,
  selection,
  onPress,
  testID,
  isAllowRecurring,
  noBorder = false,
}: {
  title: string;
  selection: boolean;
  onPress: VoidCallBack;
  testID: string;
  noBorder?: boolean;
  isAllowRecurring: boolean;
}) => {
  const textColor = isAllowRecurring
    ? theme.colors.black
    : theme.colors.gray300;
  return (
    <ListItemWrapper>
      <MenuToggleItem
        color={textColor}
        value={selection}
        onValueChange={onPress}
        menuLabel={''}
        testID={testID}
        iconName={''}
        disabled={!isAllowRecurring}
        subtitle={title}
        noBorder={noBorder}
      />
    </ListItemWrapper>
  );
};

const PermitDetails = ({
  testID,
  pmc,
  price,
  useAdditionalDiscount,
  additionalDiscount,
  promotion,
  billingFrequency,
  billingType,
  parkZone,
  salesLocation,
  validFrom,
  permitName,
  quantity,
  country,
  usageType,
  serviceFee,
}: PermitDetailsProps) => {
  const { t } = useTranslation();
  const subtitle = parkZone?.name || salesLocation?.name || '';

  return (
    <>
      <PermitTicketHeader
        title={t('longTermParking.title')}
        subtitle={subtitle}
        testID={testID}
      />
      <StyledWrapper mb={theme.normalising.heightPixel(20)}>
        <BaseText center testID={`${testID}-garage-name`}>
          {`${permitName} \u2022 ${quantity} ${
            quantity && +quantity > 1
              ? t('services.permits')
              : t('services.permit')
          }`}
        </BaseText>
      </StyledWrapper>
      {usageType === 'SKIDATA' && (
        <StyledWrapper mb={theme.normalising.heightPixel(20)}>
          <BaseText center testID={`${testID}-garage-name`}>
            {t('permit.skidataUsage')}
          </BaseText>
        </StyledWrapper>
      )}
      <SalesOrderPermiPriceInfo
        pmc={pmc}
        totalPrice={price / 100}
        serviceFee={serviceFee / 100}
        useAdditionalDiscount={useAdditionalDiscount}
        additionalDiscount={additionalDiscount}
        promotion={promotion}
        billingFrequency={billingFrequency}
        billingType={billingType}
        validFrom={validFrom || new Date()}
        country={country || 'SE'}
      />
    </>
  );
};

export const AutoRenewalView = ({
  isRecurring,
  testID,
  setIsRecurring,
  status,
  isInvoicing,
  allowRecurring,
  isRenewalDisabled = false,
  noBorder = false,
}: AutoRenewalViewProps) => {
  const { t } = useTranslation();
  const isValidStateForRecurring = status === 'ACTIVE' && allowRecurring;
  const isToggleEnabled = isValidStateForRecurring && !isRenewalDisabled;

  if (isInvoicing) {
    return <StyledWrapper />;
  }

  return (
    <SelectListItem
      title={t('permit.recurringTitle')}
      selection={isRecurring}
      onPress={setIsRecurring}
      testID={`${testID}-recurring-selection`}
      isAllowRecurring={isToggleEnabled}
      noBorder={noBorder}
    />
  );
};

const SalesOrderPermitCard = ({
  parkZone,
  pmc,
  country,
  salesLocation,
  permitName,
  price,
  useAdditionalDiscount,
  additionalDiscount,
  promotion,
  isRecurring,
  setIsRecurring,
  testID,
  billingFrequency,
  billingType,
  status,
  parkingSpaceName,
  isInvoicing,
  allowRecurring = true,
  isRenewalDisabled = false,
  scheduledTerminationDate,
  validFrom,
  validTo,
  quantity,
  usageType,
  serviceFee,
  showAttachment = false,
}: PermitCardProps) => {
  const displayRenewalToggle = billingFrequency && billingType !== 'ONE_TIME';
  const displayParkingSpace = !!parkingSpaceName;
  const { t } = useTranslation();
  const {
    params: { orderLineIdWithVersion },
  } = useRoute<RouteProp<RootStackParamList, 'salesOrderPermitDetails'>>();
  const parkingSpaces =
    useUserPermitOrderLine(orderLineIdWithVersion)?.parkingSpaces ?? [];

  const validFromText = () => {
    if (isAfter(new Date(), new Date(validFrom ?? ''))) {
      return t('longTermParking.validImmediately');
    }
    return formatPermitValid(validFrom);
  };

  const effectiveValidTo = validTo ?? scheduledTerminationDate;

  return (
    <CrickleCutCardBackground
      aboveCutView={
        <PermitDetails
          pmc={pmc}
          parkZone={parkZone}
          country={country}
          salesLocation={salesLocation}
          permitName={permitName}
          price={price}
          useAdditionalDiscount={useAdditionalDiscount}
          additionalDiscount={additionalDiscount}
          promotion={promotion}
          testID={testID}
          billingFrequency={billingFrequency}
          billingType={billingType}
          validFrom={new Date(validFrom || '')}
          validTo={validTo}
          quantity={quantity}
          usageType={usageType}
          serviceFee={serviceFee}
        />
      }
      belowCutView={
        <PermitContentWrapper>
          {displayRenewalToggle && (
            <AutoRenewalView
              testID={testID}
              isRecurring={isRecurring}
              setIsRecurring={setIsRecurring}
              status={status}
              isInvoicing={isInvoicing}
              allowRecurring={allowRecurring}
              isRenewalDisabled={isRenewalDisabled}
            />
          )}
          {validFrom && (
            <PermitElementRow
              label={t('permit.access.validFrom')}
              detail={validFromText()}
              borderColor={theme.colors.white}
            />
          )}
          {effectiveValidTo && (
            <PermitElementRow
              label={t('permit.access.validTo')}
              detail={formatPermitValid(effectiveValidTo)}
              withTopBorder={!!validFrom}
              borderColor={theme.colors.white}
            />
          )}
          {displayParkingSpace && parkingSpaceName && (
            <PermitElementRow
              label={t('longTermParking.parkingSpace')}
              detail={getParkingSpacesText(parkingSpaces as ParkingSpace[], '')}
              icon={<Icon color={theme.colors.black} name="Chevron_Right" />}
              onPress={() => {
                usePermitStore
                  .getState()
                  .setSelectedPermit(orderLineIdWithVersion);
                useUiStore.getState().setSheetModal('parkingSpaces');
              }}
              withTopBorder={true}
              borderColor={theme.colors.white}
            />
          )}
          {showAttachment && <Attachment noBorder />}
        </PermitContentWrapper>
      }
    />
  );
};

export default SalesOrderPermitCard;
